import React, { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import voiceStreamingService from '../services/voiceStreamingService';
import './PersonalityTraits.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://oauth.remote-assistant.ai';

const PersonalityTraits = ({ initialTraits = {}, onTraitsChange, selectedVoice = null, businessConfig = {} }) => {
  const [traits, setTraits] = useState({
    formality: initialTraits.formality || 'Strictly Professional',
    verbosity: initialTraits.verbosity || 'Concise',
    empathy: initialTraits.empathy || 'Polite and Neutral',
    proactivity: initialTraits.proactivity || 'Offers Clarifications',
    humor: initialTraits.humor || 'Completely Serious'
  });
  const [showCustom, setShowCustom] = useState(false);
  const [activePreset, setActivePreset] = useState('efficient');

  const presets = {
    efficient: {
      formality: 'Strictly Professional',
      verbosity: 'Concise',
      empathy: 'Polite and Neutral',
      proactivity: 'Offers Clarifications',
      humor: 'Completely Serious'
    },
    friendly: {
      formality: 'Friendly and Approachable',
      verbosity: 'Balanced',
      empathy: 'Warm and Understanding',
      proactivity: 'Suggestive',
      humor: 'Friendly and Playful'
    },
    helpful: {
      formality: 'Business Casual',
      verbosity: 'Detailed',
      empathy: 'Warm and Understanding',
      proactivity: 'Fully Proactive',
      humor: 'Occasional Dry Wit'
    }
  };

  // Check if current traits match any preset
  const checkActivePreset = (currentTraits) => {
    for (const [presetKey, presetTraits] of Object.entries(presets)) {
      const matches = Object.keys(presetTraits).every(
        key => currentTraits[key] === presetTraits[key]
      );
      if (matches) {
        setActivePreset(presetKey);
        return;
      }
    }
    setActivePreset(null);
    setShowCustom(true); // Auto-expand when no preset matches
  };

  const traitOptions = {
    formality: [
      'Strictly Professional',
      'Business Casual', 
      'Friendly and Approachable',
      'Very Casual'
    ],
    verbosity: [
      'Brief and Direct',
      'Concise',
      'Balanced',
      'Detailed',
      'Thorough'
    ],
    empathy: [
      'Purely Factual',
      'Polite and Neutral',
      'Warm and Understanding',
      'Highly Empathetic'
    ],
    proactivity: [
      'Strictly Reactive',
      'Offers Clarifications',
      'Suggestive',
      'Fully Proactive'
    ],
    humor: [
      'Completely Serious',
      'Occasional Dry Wit',
      'Friendly and Playful',
      'Genuinely Comedic'
    ]
  };

  const traitDescriptions = {
    formality: "How formal should your assistant sound? Choose between a strictly professional tone or a more casual, friendly vibe.",
    verbosity: "How much detail should your assistant provide? Choose between short, direct answers or more thorough explanations.",
    empathy: "How much emotion and understanding should your assistant show? This is especially important when dealing with frustrated or happy customers.",
    proactivity: "Should your assistant wait for specific instructions or actively suggest next steps and solutions?",
    humor: "Is it okay for your assistant to be a little playful? Adjust how much, if any, humor you'd like it to use."
  };

  const exampleResponses = {
    formality: {
      'Strictly Professional': "Certainly, Sir/Ma'am. I shall assist you with your inquiry immediately.",
      'Business Casual': "Of course! I'll be happy to help you with that.",
      'Friendly and Approachable': "Absolutely! No problem at all - I've got you covered.",
      'Very Casual': "Sure thing! I can totally get that sorted out for you!"
    },
    verbosity: {
      'Brief and Direct': "Appointment booked.",
      'Concise': "Your appointment is booked for 2 PM tomorrow.",
      'Balanced': "I've successfully booked your appointment for 2 PM tomorrow. You'll receive a confirmation email shortly.",
      'Detailed': "I've booked your appointment for 2 PM tomorrow with Dr. Smith. You'll receive a confirmation email with all the details, including the address and what to bring.",
      'Thorough': "Perfect! I've scheduled your appointment for 2 PM tomorrow with Dr. Smith at the downtown office. You'll get a confirmation email with the address, parking info, and a reminder of what to bring. Would you like me to add this to your calendar as well?"
    },
    empathy: {
      'Purely Factual': "Your appointment has been cancelled. Next available slot is Friday.",
      'Polite and Neutral': "I understand you need to cancel. I can reschedule you for Friday.",
      'Warm and Understanding': "I understand that must be frustrating. Let me help you find a new time that works better.",
      'Highly Empathetic': "Oh no, I'm so sorry this isn't working out for you! I completely understand how disappointing that must be. Let me do everything I can to find you a perfect alternative time."
    }
  };

  const handleTraitChange = async (traitName, value) => {
    const newTraits = { ...traits, [traitName]: value };
    setTraits(newTraits);
    checkActivePreset(newTraits);
    if (onTraitsChange) {
      onTraitsChange(newTraits);
    }
    
    // Auto-save personality traits when slider changes
    try {
      const configService = (await import('../services/configService')).default;
      await configService.updatePersonalityTraits(newTraits);
      console.log('✅ Auto-saved personality traits:', newTraits);
    } catch (error) {
      console.error('❌ Failed to auto-save personality traits:', error);
    }
  };

  const applyPreset = async (presetKey) => {
    const preset = presets[presetKey];
    setTraits(preset);
    setActivePreset(presetKey);
    if (onTraitsChange) onTraitsChange(preset);
    
    try {
      const configService = (await import('../services/configService')).default;
      await configService.updatePersonalityTraits(preset);
    } catch (error) {
      console.error('❌ Failed to auto-save personality preset:', error);
    }
  };

  // Check active preset on initial load
  useEffect(() => {
    checkActivePreset(traits);
  }, []);

  const getSliderValue = (traitName, value) => {
    return traitOptions[traitName].indexOf(value);
  };

  const getValueFromSlider = (traitName, sliderValue) => {
    return traitOptions[traitName][sliderValue];
  };

  return (
    <div className="personality-traits">
      {/* Personality Presets */}
      <div className="preset-buttons">
        <p className="section-description">
          Choose a personality style for your assistant:
        </p>
        <div className="preset-grid">
          <button 
            className={`preset-btn ${activePreset === 'efficient' ? 'active' : ''}`}
            onClick={() => applyPreset('efficient')}
          >
            📋 Efficient Receptionist
          </button>
          
          <button 
            className={`preset-btn ${activePreset === 'friendly' ? 'active' : ''}`}
            onClick={() => applyPreset('friendly')}
          >
            🤝 Friendly Concierge
          </button>
          
          <button 
            className={`preset-btn ${activePreset === 'helpful' ? 'active' : ''}`}
            onClick={() => applyPreset('helpful')}
          >
            🎯 Helpful Assistant
          </button>
        </div>
        
        {/* Custom Toggle Button */}
        <button 
          className={`custom-toggle-btn ${showCustom ? 'active' : ''}`}
          onClick={() => setShowCustom(!showCustom)}
        >
          ⚙️ {showCustom ? 'Hide' : 'Show'} Custom Settings
        </button>
      </div>

      {/* Custom Sliders - Only show when toggled */}
      {showCustom && (
        <div className="custom-traits">
          <p className="section-description">
            Fine-tune each personality dimension:
          </p>
          {Object.keys(traitOptions).map((traitName) => (
            <div key={traitName} className="trait-container">
              <div className="trait-header">
                <h4 className="trait-title">
                  {traitName.charAt(0).toUpperCase() + traitName.slice(1)}
                </h4>
                <span className="trait-value">{traits[traitName]}</span>
              </div>
              
              <p className="trait-description">{traitDescriptions[traitName]}</p>
              
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max={traitOptions[traitName].length - 1}
                  value={getSliderValue(traitName, traits[traitName])}
                  onChange={(e) => handleTraitChange(traitName, getValueFromSlider(traitName, parseInt(e.target.value)))}
                  className="trait-slider"
                />
                <div className="slider-labels">
                  <span className="label-left">{traitOptions[traitName][0]}</span>
                  <span className="label-right">{traitOptions[traitName][traitOptions[traitName].length - 1]}</span>
                </div>
              </div>

              {exampleResponses[traitName] && (
                <div className="example-response">
                  <strong>Example:</strong> "{exampleResponses[traitName][traits[traitName]]}"
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <PersonalityPreview traits={traits} selectedVoice={selectedVoice} businessConfig={businessConfig} />
    </div>
  );
};

const PersonalityPreview = ({ traits, selectedVoice, businessConfig }) => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('greeting');

  // Ensure WebSocket connection on component mount
  useEffect(() => {
    const ensureWebSocketConnection = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        if (token) {
          voiceStreamingService.setAuthToken(token);
          
          if (!voiceStreamingService.websocket || voiceStreamingService.websocket.readyState !== WebSocket.OPEN) {
            await voiceStreamingService.connect(token);
          }
          setWsConnected(true);
        }
      } catch (error) {
        console.error('Failed to establish WebSocket connection:', error);
        setWsConnected(false);
      }
    };

    ensureWebSocketConnection();
  }, []);

  const scenarios = [
    { key: 'greeting', label: 'Greeting', icon: '👋' },
    { key: 'hours', label: 'Business Hours', icon: '🕐' },
    { key: 'manager', label: 'Speak to Manager', icon: '👔' },
    { key: 'company', label: 'Tell me about your company', icon: '🏢' },
    { key: 'farewell', label: 'Farewell', icon: '👋' }
  ];

  const generateResponse = async () => {
    const scenario = selectedScenario;
    
    // Prevent multiple simultaneous requests
    if (loading) return;
    
    // Prevent button spam while audio is playing
    if (voiceStreamingService.isPlaying) {
      console.log('Audio already playing, ignoring button click');
      return;
    }
    
    setLoading(true);
    
    try {
      // Prevent spam clicks - check if already processing
      if (voiceStreamingService.isPlaying || voiceStreamingService.isReceivingStream) {
        console.log('Audio already playing or processing, ignoring button click');
        return;
      }
      
      // Clear audio without disconnecting WebSocket
      if (voiceStreamingService.websocket?.readyState === 1) {
        console.log('🧹 User started new personality test - clearing audio');
        voiceStreamingService.clearAudio(); // Use new method that keeps WebSocket connected
      }
      
      console.log('🎤 Testing personality scenario:', scenario);
      
      // Prepare voice config if available
      let voiceConfig = null;
      if (selectedVoice) {
        voiceConfig = {
          provider: selectedVoice.provider,
          voice_id: selectedVoice.voice_id,
          provider_config: selectedVoice.provider_config
        };
      }
      
      // Use configService business_id-based method
      const configService = (await import('../services/configService')).default;
      const response = await configService.generatePersonalityResponse(scenario, voiceConfig);
      
      console.log('📝 API Response Data:', response);
      
      if (response.status === 'streaming') {
        console.log('🎵 Backend initiated TTS streaming!');
        setResponses(prev => ({ ...prev, [scenario]: 'Audio streaming initiated...' }));
      } else {
        const text = response.response;
        setResponses(prev => ({ ...prev, [scenario]: text }));
        
        // Fallback: Stream via frontend WebSocket if backend didn't stream
        if (text && voiceStreamingService.websocket?.readyState === 1) {
          try {
            console.log('🔊 Fallback: Frontend TTS streaming');
            const voiceConfigForStream = selectedVoice ? 
              selectedVoice.provider_config : 
              { voice_id: 'alloy' };
            const provider = selectedVoice ? selectedVoice.provider : 'openai';
            
            await voiceStreamingService.streamVoicePreview(
              provider,
              voiceConfigForStream,
              text
            );
          } catch (streamError) {
            console.error('❌ TTS streaming failed:', streamError);
          }
        }
      }
    } catch (error) {
      console.error('❌ Request failed:', error);
      setResponses(prev => ({ ...prev, [scenario]: 'Error generating response' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="personality-preview">
      <h4>Try Your Assistant's Personality</h4>
      <p className="preview-description">
        See how your assistant would respond in common scenarios with your current personality settings.
      </p>
      

      
      <div className="scenario-controls">
        <div className="scenario-selector">
          <label htmlFor="scenario-dropdown">Test Scenario:</label>
          <select 
            id="scenario-dropdown"
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
            className="scenario-dropdown"
          >
            {scenarios.map(scenario => (
              <option key={scenario.key} value={scenario.key}>
                {scenario.icon} {scenario.label}
              </option>
            ))}
          </select>
        </div>
        
        <button 
          className="preview-btn"
          onClick={generateResponse}
          disabled={loading}
          title={!wsConnected ? 'WebSocket not connected - audio may not work' : ''}
        >
          {loading ? '⏳ Playing...' : '🎵 Preview Voice'}
        </button>
      </div>
      
      {responses[selectedScenario] && (
        <div className="scenario-response">
          <strong>Assistant ({scenarios.find(s => s.key === selectedScenario)?.label}):</strong> "{responses[selectedScenario]}"
        </div>
      )}
    </div>
  );
};

export default PersonalityTraits;